import React, { CSSProperties } from 'react';
import hingeIcon from './assets/timer-hinge.svg';
import separatorIcon from './assets/timer-separator.svg';
import { TimeLeft } from '../../../stickyBanner.helper';

/** Timer tiles, left to right (positions from the Figma flip timer, in px). */
const TIMER_UNITS = [
	{ key: 'days', label: 'DAYS', left: 0, labelLeft: 12.93 },
	{ key: 'hours', label: 'HOURS', left: 48.82, labelLeft: 57.59 },
	{ key: 'minutes', label: 'MINUTES', left: 97.64, labelLeft: 102.89 },
	{ key: 'seconds', label: 'SECONDS', left: 146.34, labelLeft: 150.74 },
] as const;

/** Dots between the tiles. */
const SEPARATOR_LEFTS = [ 46.55, 95.37, 144.18 ];

/**
 * Darker shade of a hex color (for the folding flap gradient).
 *
 * @param hex    Color like #1BAA76.
 * @param amount 0..1, how much darker.
 */
const shade = ( hex: string, amount: number ): string => {
	const match = /^#?([0-9a-f]{6})$/i.exec( hex );
	if ( ! match ) {
		return hex;
	}
	const num = parseInt( match[ 1 ], 16 );
	const channel = ( shift: number ) =>
		Math.round( ( ( num >> shift ) & 255 ) * ( 1 - amount ) )
			.toString( 16 )
			.padStart( 2, '0' );
	return `#${ channel( 16 ) }${ channel( 8 ) }${ channel( 0 ) }`;
};

interface TileProps {
	value: string;
}

/**
 * One tile: full digits on two halves, plus a flap that folds down over
 * the top half whenever the value changes (it ends flat, so the digits
 * are never left cut or squeezed).
 */
const Tile: React.FC< TileProps > = ( { value } ) => {
	const digitsClass = `sb-flip-digits${ value.length > 2 ? ' is-long' : '' }`;

	return (
		<>
			<span className="sb-flip-half is-top" />
			<span className="sb-flip-half is-bottom" />
			<span className={ digitsClass }>{ value }</span>
			{ /* Re-mounted on every change so the flip animation replays. */ }
			<span className="sb-flip-flap" key={ value }>
				<span className={ digitsClass }>{ value }</span>
			</span>
			<img className="sb-flip-hinge" src={ hingeIcon } alt="" />
		</>
	);
};

interface FlipTimerProps {
	timeLeft: TimeLeft;
	tileColor: string;
	digitColor?: string;
	unitColor: string;
}

/** Flip-clock timer: days, hours, minutes, seconds. */
const FlipTimer: React.FC< FlipTimerProps > = ( {
	timeLeft,
	tileColor,
	digitColor,
	unitColor,
} ) => {
	const values: Record< ( typeof TIMER_UNITS )[ number ][ 'key' ], string > =
		{
			days: timeLeft.days.padStart( 2, '0' ),
			hours: timeLeft.hoursOfDay,
			minutes: timeLeft.minutes,
			seconds: timeLeft.seconds,
		};

	const style = {
		'--sb-flip-tile': tileColor,
		'--sb-flip-flap-from': shade( tileColor, 0.12 ),
		'--sb-flip-flap-to': shade( tileColor, 0.42 ),
		'--sb-flip-digit': digitColor || undefined,
		'--sb-flip-unit': unitColor,
	} as CSSProperties;

	return (
		<div
			className="sb-flip"
			style={ style }
			role="timer"
			aria-label={ TIMER_UNITS.map(
				( unit ) =>
					`${ values[ unit.key ] } ${ unit.label.toLowerCase() }`
			).join( ', ' ) }
		>
			{ SEPARATOR_LEFTS.map( ( left ) => (
				<img
					key={ left }
					className="sb-flip-separator"
					src={ separatorIcon }
					alt=""
					style={ { left } }
				/>
			) ) }
			{ TIMER_UNITS.map( ( unit ) => (
				<div
					key={ unit.key }
					className="sb-flip-tile"
					style={ { left: unit.left } }
					aria-hidden
				>
					<Tile value={ values[ unit.key ] } />
				</div>
			) ) }
			{ TIMER_UNITS.map( ( unit ) => (
				<span
					key={ unit.key }
					className="sb-flip-unit"
					style={ { left: unit.labelLeft } }
					aria-hidden
				>
					{ unit.label }
				</span>
			) ) }
		</div>
	);
};

export default FlipTimer;
